import { fallBackToSource, sizedImageUrl } from "../lib/imageUrl.js";

/**
 * A small round user avatar with a deterministic initials fallback: when there
 * is no `url`, it renders the first letter of `name` on a colour derived from
 * the name, so every commenter/actor always has a visual avatar (never a bare
 * name). `size` sets both dimensions in px; extra classes let callers keep the
 * existing per-surface sizing/border rules.
 */
export function Avatar({
  name,
  url,
  size = 28,
  className
}: {
  name: string;
  url?: string | null;
  size?: number;
  className?: string;
}) {
  const cls = className ? `avatar ${className}` : "avatar";
  if (url) {
    return <img className={cls} src={sizedImageUrl(url, size, { aspect: 1 })} onError={fallBackToSource(url)} alt="" width={size} height={size} />;
  }
  const initial = firstLetter(name);
  return (
    <span
      className={`${cls} avatar--fallback`}
      style={{ width: size, height: size, background: colorFor(name), fontSize: size * 0.44 }}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}

/** First visible character of a name, upper-cased; "?" when empty. */
function firstLetter(name: string): string {
  const trimmed = (name ?? "").trim();
  return trimmed ? [...trimmed][0].toUpperCase() : "?";
}

// A small, readable palette (muted against the dark UI). Pick deterministically
// from the name so the same author keeps the same colour everywhere.
const PALETTE = ["#c89040", "#52a878", "#5a86c8", "#b06a9c", "#c8695a", "#6aa8b0", "#9c86c8", "#b09a52"];

function colorFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
}
